import { useEffect, useState } from 'react';

interface ResponsiveInfo {
	width: number;
	height: number;
	/** Active Tailwind breakpoint bucket derived from width (640/768/1024/1280 thresholds) */
	breakpoint: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
	isMobile: boolean;
	isTablet: boolean;
	isDesktop: boolean;
	isSmallScreen: boolean;
	isLargeScreen: boolean;
	shouldShowSidebar: boolean;
	shouldShowHamburger: boolean;
	// Legacy properties for backward compatibility
	isSmall: boolean;
	isMedium: boolean;
	isLarge: boolean;
	isXLarge: boolean;
}

export const useResponsive = (): ResponsiveInfo => {
	const [dimensions, setDimensions] = useState({
		width: window.innerWidth,
		height: window.innerHeight,
	});

	useEffect(() => {
		const handleResize = () => {
			setDimensions({
				width: window.innerWidth,
				height: window.innerHeight,
			});
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const { width, height } = dimensions;

	// Define breakpoints to match Tailwind and navigation behavior
	const isMobile = width < 768;
	const isTablet = width >= 768 && width < 1024;
	const isDesktop = width >= 1024;

	// Tailwind breakpoint bucket for consumers that need per-breakpoint values
	// (e.g. the list-builder grid's authored column counts)
	const breakpoint: ResponsiveInfo['breakpoint'] =
		width < 640
			? 'xs'
			: width < 768
				? 'sm'
				: width < 1024
					? 'md'
					: width < 1280
						? 'lg'
						: 'xl';

	return {
		width,
		height,
		breakpoint,
		isMobile,
		isTablet,
		isDesktop,
		// Convenience flags
		isSmallScreen: isMobile,
		isLargeScreen: isDesktop,
		// Navigation-specific flags
		shouldShowSidebar: isDesktop, // Sidebar visible on lg+ screens (>= 1024px)
		shouldShowHamburger: !isDesktop, // Hamburger visible on < 1024px screens
		// Legacy properties for backward compatibility
		isSmall: width < 640, // sm breakpoint
		isMedium: width >= 640 && width < 768, // md breakpoint
		isLarge: width >= 768 && width < 1024, // lg breakpoint
		isXLarge: width >= 1024, // xl breakpoint
	};
};
